import { format } from 'date-fns';

function formatReadDate(raw) {
    if (!raw) return '—';
    try {
        return format(new Date(raw), 'd MMM yyyy');
    } catch {
        return '—';
    }
}

function accessGroupLabel(raw) {
    if (!raw) return '—';
    const text = String(raw).trim();
    const colon = text.indexOf(':');
    if (colon >= 0) {
        const name = text.slice(colon + 1).trim();
        return name || text;
    }
    return text;
}

export function readersFromFile(file, projectUsersAll = []) {
    const ids = Array.isArray(file?.read_by) ? file.read_by : [];
    const readDateByMember = new Map();
    (file?.read_by_details || []).forEach(row => {
        if (row?.group_member_id != null) {
            readDateByMember.set(String(row.group_member_id), row.read_date);
        }
    });
    const fallbackDate = file?.read_date || null;
    return ids.map(id => {
        const row = projectUsersAll.find(item => String(item?.value?.group_member_id) === String(id));
        const perReaderDate = readDateByMember.get(String(id));
        const lastRead = formatReadDate(perReaderDate || (readDateByMember.size ? null : fallbackDate));
        return {
            id,
            name: row?.value?.full_name || 'Unknown',
            accessGroup: accessGroupLabel(row?.group),
            lastRead,
        };
    });
}

export function documentIdOf(value) {
    if (value == null) return null;
    if (typeof value === 'object') return value.id ?? value.pk ?? null;
    return value;
}

export function irlStatusLabel(item) {
    if (item?.irresponse?.answer) return 'Answered';
    if (item?.status === true || item?.status === 'answered') return 'Answered';
    return 'In progress';
}

export function relatedForDocuments(documentIds, informationRequests = [], observations = []) {
    const ids = new Set((documentIds || []).filter(id => id != null).map(id => String(id)));
    const irl = (informationRequests || [])
        .filter(item => ids.has(String(documentIdOf(item?.referenced_file))))
        .map(item => ({
            id: item.id,
            label: item.prefix ? `${item.prefix}${item.item ?? ''}` : `IRL-${item.id}`,
            title: item.request || item.title || 'Information request',
            status: irlStatusLabel(item),
        }));
    const obs = (observations || [])
        .filter(item => ids.has(String(documentIdOf(item?.docitem))))
        .map(item => ({
            id: item.id,
            label: `OBS-${item.id}`,
            title: item.observation || 'Observation',
            description: item.comments || '',
        }));
    return { irl, obs };
}

export function observationsForRecording(recordingId, observations = []) {
    if (!recordingId) return [];
    return (observations || [])
        .filter(item => String(item?.meeting_recording) === String(recordingId))
        .map(item => ({
            id: item.id,
            label: `OBS-${item.id}`,
            title: item.observation || 'Observation',
            description: item.comments || '',
        }));
}

function textMatches(query, parts) {
    const needle = String(query || '').trim().toLowerCase();
    if (!needle) return false;
    return parts
        .filter(part => part != null && part !== '')
        .join(' ')
        .toLowerCase()
        .includes(needle);
}

export function mailSearchHits(query, mails = []) {
    return mails
        .filter(mail =>
            textMatches(query, [mail.subject, mail.senderName, mail.senderEmail, mail.body, ...(mail.attachments || []).map(item => item.name)]),
        )
        .map(mail => ({
            id: `mail-${mail.id}`,
            kind: 'mail',
            sourceId: mail.id,
            document_name: mail.subject || 'Forwarded email',
            path: 'Project Mail',
            snippet: String(mail.body || '').replace(/\s+/g, ' ').trim().slice(0, 180),
            status: mail.status,
        }));
}

const TEAMS_CUE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(\/\d+-\d+)?$/i;
const WEBVTT_CUE_TIME = /^((?:\d{1,2}:)?\d{2}:\d{2}[.,]\d{1,3})\s*-->\s*(?:\d{1,2}:)?\d{2}:\d{2}/;
const WEBVTT_VOICE_TAG = /^<v\s+([^>]+)>([\s\S]*?)<\/v>$/i;

export function formatTranscriptSpeaker(name) {
    const trimmed = String(name || '').trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('@')) {
        const suffix = trimmed.slice(1).trim();
        return suffix ? `Speaker ${suffix}` : 'Speaker';
    }
    return trimmed;
}

export function formatTranscriptTime(time) {
    const raw = String(time || '').trim();
    if (!raw) return '';
    const withoutMs = raw.replace(/[.,]\d{1,3}$/, '');
    const parts = withoutMs.split(':');
    if (parts.length === 2) {
        return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    }
    if (parts.length === 3) {
        return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:${parts[2].padStart(2, '0')}`;
    }
    return withoutMs;
}

export function formatTranscriptQuote(text) {
    const trimmed = String(text || '').trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('"') && trimmed.endsWith('"')) return trimmed;
    return `"${trimmed}"`;
}

export function formatTranscriptDocument(lines = []) {
    return lines
        .map(line => {
            const time = formatTranscriptTime(line?.time);
            const speaker = formatTranscriptSpeaker(line?.speaker);
            const head = [time, speaker].filter(Boolean).join(' ');
            const text = String(line?.text || '').trim();
            if (!text) return '';
            return head ? `${head}\n${text}` : text;
        })
        .filter(Boolean)
        .join('\n\n');
}

function cleanFilenamePart(value) {
    return String(value || '')
        .replace(/[\\/:*?"<>|]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

export function transcriptDownloadFilename({ projectName, title, date } = {}) {
    const project = cleanFilenamePart(projectName);
    let meeting = cleanFilenamePart(title) || 'Meeting transcript';
    const meetingDate = cleanFilenamePart(date);
    const join = () => [project, meeting, meetingDate].filter(Boolean).join(' - ');
    let name = join();
    const max = 180;
    if (name.length > max) {
        const room = Math.max(20, max - (name.length - meeting.length));
        meeting = meeting.slice(0, room).trim();
        name = join();
    }
    return `${name || 'Meeting transcript'}.txt`;
}

const CLOCK = String.raw`\d{1,2}:\d{1,2}(?::\d{1,2})?(?:[.,]\d{1,3})?`;
const BARE_INT = /^\d{3,9}$/;
const CLOCK_ONLY = new RegExp(`^${CLOCK}$`);
const SPEAKER_TIME = new RegExp(`^(?<speaker>.+?)\\s+(?<time>${CLOCK})$`);
const INLINE_TURN = new RegExp(
    `(^|\\s)(?<speaker>Speaker\\s+\\d+|[A-Z][\\w'.’@-]*(?:\\s+[A-Z][\\w'.’@-]*){0,3})\\s+(?<time>${CLOCK})(?!\\s*-->)(?=\\s|$)`,
    'g',
);
const TURN_STOPWORDS = new Set(['okay', 'yeah', 'yes', 'no', 'so', 'all', 'and', 'the', 'this', 'that', 'right', 'well', 'oh', 'um', 'uh']);
const TIME_SPEAKER = new RegExp(`^(?<time>${CLOCK})\\s+(?<speaker>.+)$`);
const TIME_RANGE = new RegExp(`^(?<time>${CLOCK})\\s*-->\\s*${CLOCK}$`);
const SPEAKER_COLON = /^(?<speaker>[^:\n]{1,80}?):\s+(?<text>\S.*)$/;
const SPEAKER_WORDS = /^[\w@][\w.'’@-]*(?:\s+[\w@][\w.'’@-]*){0,4}$/;
const TRANSCRIPT_DATE = /^(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},\s+\d{4}\b/i;
const TRANSCRIPT_DURATION = /^(?:(\d+)h\s*)?(\d+)m\s*(\d+)s$/i;

function cleanSpoken(text) {
    return String(text || '')
        .replace(/(?:\s+\d{5,9})+\s*$/g, '')
        .trim();
}

function clockLabel(value) {
    const cleaned = String(value || '').trim().replace(',', '.');
    const main = cleaned.split('.')[0];
    const parts = main.split(':').map(part => Number.parseInt(part, 10));
    if (!parts.length || parts.some(part => Number.isNaN(part))) return '';
    let hours = 0;
    let minutes = 0;
    let seconds = 0;
    if (parts.length === 2) {
        minutes = parts[0];
        seconds = parts[1];
    } else if (parts.length === 3) {
        hours = parts[0];
        minutes = parts[1];
        seconds = parts[2];
    } else {
        return '';
    }
    return [hours, minutes, seconds].map(part => String(part).padStart(2, '0')).join(':');
}

function secondsClock(totalSeconds) {
    const total = Math.floor(Number(totalSeconds) || 0);
    if (total < 0) return '';
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    return [hours, minutes, seconds].map(part => String(part).padStart(2, '0')).join(':');
}

function durationSeconds(line) {
    const match = String(line || '').trim().match(TRANSCRIPT_DURATION);
    if (!match) return null;
    const hours = Number(match[1] || 0);
    const minutes = Number(match[2] || 0);
    const seconds = Number(match[3] || 0);
    const total = hours * 3600 + minutes * 60 + seconds;
    return total || null;
}

function plausibleSpeaker(name) {
    const cleaned = String(name || '').trim();
    if (!cleaned || cleaned.length > 60) return false;
    if (BARE_INT.test(cleaned) || CLOCK_ONLY.test(cleaned)) return false;
    if (durationSeconds(cleaned) || TRANSCRIPT_DATE.test(cleaned)) return false;
    if (cleaned.toLowerCase().includes('meeting recording')) return false;
    return SPEAKER_WORDS.test(cleaned);
}

function turnSpeaker(name) {
    const cleaned = String(name || '').trim();
    if (!plausibleSpeaker(cleaned)) return false;
    const words = cleaned.split(/\s+/);
    if (words.length === 1 && TURN_STOPWORDS.has(words[0].toLowerCase())) return false;
    return true;
}

function inlineTurnMatches(text) {
    const matches = [];
    const pattern = new RegExp(INLINE_TURN.source, 'g');
    let match = pattern.exec(text || '');
    while (match) {
        if (turnSpeaker(match.groups.speaker)) {
            const lead = match[1] ? match[1].length : 0;
            matches.push({
                index: match.index + lead,
                end: match.index + match[0].length,
                speaker: match.groups.speaker.trim(),
                time: match.groups.time,
            });
        }
        if (match.index === pattern.lastIndex) pattern.lastIndex += 1;
        match = pattern.exec(text || '');
    }
    return matches;
}

function parseInlineTurns(text) {
    const matches = inlineTurnMatches(text);
    if (!matches.length) return [];
    const cues = [];
    matches.forEach((item, index) => {
        const end = matches[index + 1]?.index ?? text.length;
        const spoken = cleanSpoken(text.slice(item.end, end).replace(/\s+/g, ' '));
        if (!spoken) return;
        cues.push({ time: clockLabel(item.time), speaker: item.speaker, text: spoken });
    });
    return cues;
}

function expandLine(line) {
    const text = String(line?.text || '');
    const matches = inlineTurnMatches(text);
    if (!matches.length) return [line];
    const cues = [];
    const head = cleanSpoken(text.slice(0, matches[0].index));
    if (head) cues.push({ ...line, text: head });
    matches.forEach((item, index) => {
        const end = matches[index + 1]?.index ?? text.length;
        const spoken = cleanSpoken(text.slice(item.end, end));
        if (!spoken) return;
        cues.push({ time: clockLabel(item.time), speaker: item.speaker, text: spoken });
    });
    return cues.length ? cues : [line];
}

function parseSpeakerCues(lines) {
    const cues = [];
    let current = null;
    let pendingName = '';
    let pendingTime = '';

    const flush = () => {
        if (!current) return;
        const text = cleanSpoken(current.parts.filter(Boolean).join(' '));
        if (text) cues.push({ time: current.time, speaker: current.speaker, text });
        current = null;
    };
    const begin = (speaker, timeRaw, first = '') => {
        flush();
        pendingName = '';
        pendingTime = '';
        const cue = { time: clockLabel(timeRaw), speaker: String(speaker || '').trim(), parts: [] };
        if (String(first || '').trim()) cue.parts.push(String(first).trim());
        return cue;
    };

    lines.forEach(line => {
        if (BARE_INT.test(line)) return;
        if (durationSeconds(line) && !current && cues.length === 0) {
            pendingName = '';
            return;
        }
        if (TRANSCRIPT_DATE.test(line) && !current) {
            pendingName = '';
            return;
        }
        const ranged = line.match(TIME_RANGE);
        if (ranged) {
            flush();
            pendingName = '';
            pendingTime = ranged.groups.time;
            return;
        }
        const speakerTime = line.match(SPEAKER_TIME);
        if (speakerTime && plausibleSpeaker(speakerTime.groups.speaker)) {
            current = begin(speakerTime.groups.speaker, speakerTime.groups.time);
            return;
        }
        const timeSpeaker = line.match(TIME_SPEAKER);
        if (timeSpeaker && plausibleSpeaker(timeSpeaker.groups.speaker)) {
            current = begin(timeSpeaker.groups.speaker, timeSpeaker.groups.time);
            return;
        }
        if (CLOCK_ONLY.test(line) && pendingName) {
            current = begin(pendingName, line);
            return;
        }
        if (pendingTime && !current && plausibleSpeaker(line)) {
            current = begin(line, pendingTime);
            return;
        }
        if (current) {
            current.parts.push(line);
            pendingName = '';
            return;
        }
        pendingName = plausibleSpeaker(line) ? line : '';
    });
    flush();
    return cues;
}

function parseColonCues(lines) {
    const content = [];
    const cues = [];
    lines.forEach(line => {
        if (TRANSCRIPT_DATE.test(line) || durationSeconds(line)) return;
        content.push(line);
        const match = line.match(SPEAKER_COLON);
        if (!match || !plausibleSpeaker(match.groups.speaker)) return;
        const text = match.groups.text.trim();
        if (text) cues.push({ time: '', speaker: match.groups.speaker.trim(), text });
    });
    if (cues.length !== content.length || cues.length === 0) return [];
    if (cues.length === 1 && !cues[0].speaker.includes(' ')) return [];
    return cues;
}

function pickOffsetMs(nums, durationMs) {
    let candidates = nums.slice();
    if (durationMs != null) {
        const inMeeting = candidates.filter(num => num >= 0 && num <= durationMs);
        if (inMeeting.length) candidates = inMeeting;
    }
    if (!candidates.length) return null;
    return candidates[candidates.length - 1];
}

function parseNumericCues(lines) {
    if (lines.filter(line => BARE_INT.test(line)).length < 2) return [];
    const cues = [];
    let durationMs = null;
    let index = 0;
    while (index < lines.length) {
        const line = lines[index];
        const duration = durationSeconds(line);
        if (duration && cues.length === 0) {
            durationMs = duration * 1000;
            index += 1;
            continue;
        }
        if (!BARE_INT.test(line)) {
            index += 1;
            continue;
        }
        const nums = [];
        while (index < lines.length && BARE_INT.test(lines[index])) {
            nums.push(Number(lines[index]));
            index += 1;
        }
        if (index >= lines.length) break;
        const speaker = lines[index];
        if (!plausibleSpeaker(speaker)) continue;
        index += 1;
        const parts = [];
        while (index < lines.length && !BARE_INT.test(lines[index])) {
            const part = lines[index];
            index += 1;
            if (TRANSCRIPT_DATE.test(part) || durationSeconds(part)) continue;
            parts.push(part);
        }
        const text = parts.join(' ').trim();
        if (!text) continue;
        const offset = pickOffsetMs(nums, durationMs);
        const time = offset != null && offset <= 24 * 3600 * 1000 ? secondsClock(offset / 1000) : '';
        cues.push({ time, speaker, text });
    }
    return cues;
}

function structureUnlabeledTranscript(transcript) {
    const unlabeled = transcript.every(line => !String(line?.speaker || '').trim() && !String(line?.time || '').trim());
    if (!unlabeled) return null;
    const lines = transcript.map(line => String(line?.text || '').trim()).filter(Boolean);
    const speakerCues = parseSpeakerCues(lines);
    const inlineCues = parseInlineTurns(lines.filter(line => !BARE_INT.test(line)).join('\n'));
    if (inlineCues.length && inlineCues.length > speakerCues.length) return inlineCues;
    if (speakerCues.length) return speakerCues;
    const colonCues = parseColonCues(lines);
    if (colonCues.length) return colonCues;
    const numericCues = parseNumericCues(lines);
    if (numericCues.length) return numericCues;
    return null;
}

/** Repair transcripts saved before MS Teams WebVTT parsing was fixed. */
export function normalizeRecordingTranscript(transcript) {
    if (!Array.isArray(transcript) || transcript.length === 0) return [];
    const looksBroken = transcript.some(line => {
        const text = String(line?.text || '').trim();
        return WEBVTT_VOICE_TAG.test(text) || TEAMS_CUE_ID.test(text) || WEBVTT_CUE_TIME.test(text);
    });
    const polishLine = line => ({
        ...line,
        speaker: formatTranscriptSpeaker(line?.speaker),
        text: cleanSpoken(line?.text),
    });

    if (!looksBroken) {
        const structured = structureUnlabeledTranscript(transcript);
        const base = structured || transcript;
        return base.flatMap(expandLine).map(polishLine).filter(line => String(line.text || '').trim());
    }

    const out = [];
    let pendingTime = '';
    transcript.forEach(line => {
        let text = String(line?.text || '').trim();
        if (!text || text.toUpperCase() === 'WEBVTT') return;
        if (TEAMS_CUE_ID.test(text)) return;

        const timeMatch = text.match(WEBVTT_CUE_TIME);
        if (timeMatch) {
            pendingTime = timeMatch[1].replace(',', '.');
            return;
        }

        let speaker = line?.speaker || '';
        let time = line?.time || pendingTime;
        const voiceMatch = text.match(WEBVTT_VOICE_TAG);
        if (voiceMatch) {
            speaker = speaker || formatTranscriptSpeaker(voiceMatch[1]);
            text = voiceMatch[2].trim();
        }
        pendingTime = '';
        if (text) {
            out.push({
                time,
                speaker: formatTranscriptSpeaker(speaker),
                text,
            });
        }
    });
    return (out.length ? out : transcript || []).map(polishLine);
}

export function recordingSearchHits(query, recordings = []) {
    return recordings
        .filter(recording =>
            textMatches(query, [
                recording.title,
                recording.source,
                ...(recording.participantNames || []),
                ...normalizeRecordingTranscript(recording.transcript || []).map(
                    line => `${line.speaker || ''} ${line.text || ''}`,
                ),
                ...(recording.aiSnapshot?.keyTopics || []),
                ...(recording.aiSnapshot?.keyInformation || []),
            ]),
        )
        .map(recording => ({
            id: `recording-${recording.id}`,
            kind: 'recording',
            sourceId: recording.id,
            document_name: recording.title || 'Meeting recording',
            path: 'Meeting Recordings',
            snippet: normalizeRecordingTranscript(recording.transcript || [])[0]?.text || '',
            status: recording.status,
        }));
}

export function stripHtml(value) {
    return String(value || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function tryParseJson(value) {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
    try {
        return JSON.parse(trimmed);
    } catch {
        return null;
    }
}

function normalizeSections(sections) {
    if (!sections) return {};
    if (typeof sections === 'string') {
        return tryParseJson(sections) || {};
    }
    return sections;
}

function pickSnapshotField(sections) {
    return (
        sections.quick_snapshot ||
        sections.quick_snapshot_html ||
        sections.executive_summary_html ||
        sections.executive_summary ||
        ''
    );
}

export function snapshotText(analysis) {
    if (!analysis || analysis.has_analysis === false) return '';
    const sections = normalizeSections(analysis.sections);
    let raw = pickSnapshotField(sections);
    if (typeof raw !== 'string') raw = String(raw || '');

    const embedded = tryParseJson(raw);
    if (embedded && typeof embedded === 'object') {
        raw = pickSnapshotField(embedded);
    }

    return stripHtml(raw);
}
